// common.js
// Standardized global utilities for LukaIN platform

(function () {
  var THIRD_PARTY_PATTERNS = [
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /edge-extension:\/\//i,
    /domInject\.js/i,
    /contentscript\.js/i,
    /for-embedded-agent\.js/i,
    /noupe\.com/i,
    /clientKey/i,
    /The target origin provided/i,
    /postMessage/i,
    /message port closed/i,
    /ObjectMultiplex/i,
    /MaxListenersExceededWarning/i,
    /orphaned data for stream/i,
    /Fullstory session cannot be created/i,
    /Grammarly/i,
    /grammarly-check/i,
    /AssistantLoadState/i,
    /has already been declared/i,
    /Permissions policy violation/i,
    /unload is not allowed/i,
    /DOMException/i,
    /SecurityError/i,
    /Failed to execute 'postMessage'/i,
    /Failed to execute "postMessage"/i,
    /portalInitialSuggestionsPromise/i,
    /Page data capture skipped/i,
    /answerCurrentQuestion/i,
    /capturePageData/i,
    /DEFAULT root logger/i,
    /Using DEFAULT root logger/i,
    /script\.onload/i,
    /01a08e168278700089a53912cc5a364e23c3/i,
    /Error handling response/i,
    /resumeStreamIfNeeded/i,
    /shouldResume/i,
    /screen-sharing-active/i,
    /isScreenSharingActive/i,
    /This chat is recorded/i,
    /AI Terms/i,
  ];

  function containsAnyFingerprint(args) {
    if (!args || args.length === 0) return false;
    for (var i = 0; i < args.length; i++) {
      var a = args[i];
      var s;
      if (a == null) continue;
      if (typeof a === "string") s = a;
      else if (a instanceof Error) s = String(a.message || "") + " " + String(a.stack || "");
      else {
        try { s = String(a); } catch (_e) { void _e; continue; }
      }
      for (var j = 0; j < THIRD_PARTY_PATTERNS.length; j++) {
        if (THIRD_PARTY_PATTERNS[j].test(s)) return true;
      }
    }
    return false;
  }

  var globalConsole = typeof console !== "undefined" ? console : null;
  if (globalConsole) {
    var METHODS = ["log", "warn", "error", "info", "debug", "trace"];
    for (var m = 0; m < METHODS.length; m++) {
      var name = METHODS[m];
      var orig = globalConsole[name];
      if (typeof orig !== "function") continue;
      (function (methodName, original) {
        globalConsole[methodName] = function () {
          if (containsAnyFingerprint(arguments)) return;
          return original.apply(globalConsole, arguments);
        };
      })(name, orig);
    }
  }

  function isThirdPartyNoise(msg, url, lineNo, columnNo, error) {
    var haystack = String(msg || "") + " " + String(url || "") + " ";
    if (error && error.stack) haystack += String(error.stack);
    for (var i = 0; i < THIRD_PARTY_PATTERNS.length; i++) {
      if (THIRD_PARTY_PATTERNS[i].test(haystack)) return true;
    }
    return false;
  }

  var prevOnError = window.onerror;
  window.onerror = function (msg, url, lineNo, columnNo, error) {
    if (isThirdPartyNoise(msg, url, lineNo, columnNo, error)) return true;
    if (typeof prevOnError === "function") {
      try {
        return prevOnError(msg, url, lineNo, columnNo, error);
      } catch (_e) { void _e; }
    }
    return false;
  };

  var prevOnRejection = window.onunhandledrejection;
  window.onunhandledrejection = function (event) {
    var reason = "";
    if (event) {
      if (typeof event.reason === "string") reason = event.reason;
      else if (event.reason) {
        reason =
          String(event.reason.message || "") +
          " " +
          String(event.reason.stack || "");
      }
    }
    if (isThirdPartyNoise(reason, "", 0, 0, event && event.reason)) {
      if (event && typeof event.preventDefault === "function") {
        try { event.preventDefault(); } catch (_e) { void _e; }
      }
      return;
    }
    if (typeof prevOnRejection === "function") {
      try { prevOnRejection(event); } catch (_e) { void _e; }
    }
  };
})();

import {
  get_parsed_jwt_identity,
  showGlobalPreloader,
  hideGlobalPreloader,
  initializeSidebar,
  showToast,
  formatDate,
  showCustomConfirm,
} from "./utils.js";
import { convertSelectsToCustomDropdowns, initializeDropdown } from "./dropdown.js";

// ── PHASE 9 SECURITY REMEDIATION: DOM XSS helpers ──────────────────────
// Escape any untrusted plain-text string before it reaches innerHTML.
// Prefer createElement()+textContent() where possible; use escapeHtml()
// only when a markup template genuinely requires string interpolation.
function escapeHtml(unsafe) {
  if (unsafe === null || unsafe === undefined) return "";
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Deprecated: use createElement + textContent directly instead.
// Kept as a defense-in-drop for legacy rows that still use string templates.
function setSafeInnerHTML(el, htmlString, trustedContext) {
  if (!el) return;
  const purified =
    typeof window.DOMPurify !== "undefined" && window.DOMPurify.sanitize
      ? window.DOMPurify.sanitize(htmlString, trustedContext || {})
      : htmlString;
  el.innerHTML = purified;
}

export { escapeHtml, setSafeInnerHTML };

// Attach to window for global access in HTML inline scripts
window.get_parsed_jwt_identity = get_parsed_jwt_identity;
window.showGlobalPreloader = showGlobalPreloader;
window.hideGlobalPreloader = hideGlobalPreloader;
window.initializeSidebar = initializeSidebar;
window.showToast = showToast;
window.formatDate = formatDate;
window.showCustomConfirm = showCustomConfirm;
window.escapeHtml = escapeHtml;
window.setSafeInnerHTML = setSafeInnerHTML;

// --- Global State ---
window.originalFetch = window.fetch;
var originalFetch = window.fetch; // Keep local for the wrapper below

document.addEventListener("DOMContentLoaded", () => {
  // 1. Unified Navigation Toggle (Sidebar and Public Navbar)
  document.addEventListener("click", (e) => {
    // A. Sidebar Toggle (Dashboards)
    const sidebarToggle = e.target.closest(".sidebar-toggle");
    const sidebar = document.querySelector(".sidebar");
    if (sidebarToggle && sidebar) {
      e.stopPropagation();
      const isActive = sidebar.classList.toggle("active");
      sidebarToggle.classList.toggle("is-active", isActive);
      document.body.classList.toggle("sidebar-open", isActive);

      // Create or toggle overlay
      let overlay = document.querySelector(".sidebar-overlay");
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = "sidebar-overlay";
        document.body.appendChild(overlay);
      }
      overlay.style.display = isActive ? "block" : "none";
    }

    // Close sidebar when clicking overlay
    if (e.target.classList.contains("sidebar-overlay")) {
      const sidebar = document.querySelector(".sidebar");
      const sidebarToggle = document.querySelector(".sidebar-toggle");
      if (sidebar) sidebar.classList.remove("active");
      if (sidebarToggle) sidebarToggle.classList.remove("is-active");
      document.body.classList.remove("sidebar-open");
      e.target.style.display = "none";
    }

    // B. Public Navbar Toggle (Landing, About, etc.)
    const menuToggle = e.target.closest(".menu-toggle");
    const navLinks = document.querySelector(".nav-links");
    if (menuToggle) {
      e.stopPropagation();
      const isOpen = navLinks ? navLinks.classList.toggle("nav-open") : false;
      menuToggle.classList.toggle("active", isOpen);
      menuToggle.classList.toggle("is-active", isOpen);
    }
  });

  // Close menu when clicking outside
  document.addEventListener("click", (e) => {
    const navLinks = document.querySelector(".nav-links");
    const menuToggle = document.querySelector(".menu-toggle");
    if (
      navLinks &&
      navLinks.classList.contains("nav-open") &&
      !e.target.closest(".nav-links") &&
      !e.target.closest(".menu-toggle")
    ) {
      navLinks.classList.remove("nav-open");
      if (menuToggle) {
        menuToggle.classList.remove("active");
        menuToggle.classList.remove("is-active");
      }
    }
  });

  // Initial sidebar state
  function setInitialSidebarState() {
    const sidebar = document.querySelector(".sidebar");
    if (!sidebar) return;

    if (window.innerWidth > 1024) {
      sidebar.classList.add("active");
      document.body.classList.add("sidebar-open");
    } else {
      sidebar.classList.remove("active");
      document.body.classList.remove("sidebar-open");
    }
  }

  setInitialSidebarState();
  window.addEventListener("resize", setInitialSidebarState);

  // --- Dropdown Initialization ---
  // Convert selects FIRST
  convertSelectsToCustomDropdowns();

  // Initialize any hardcoded custom dropdowns
  document.querySelectorAll(".custom-dropdown").forEach(initializeDropdown);

  // Observer for dynamic content
  const observer = new MutationObserver(convertSelectsToCustomDropdowns);
  observer.observe(document.body, { childList: true, subtree: true });

  // Close all dropdowns if clicked outside
  window.addEventListener("click", (e) => {
    document.querySelectorAll(".custom-dropdown.open").forEach((dropdown) => {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove("open");
      }
    });
  });

  // Toast Container initialization
  const existing = document.getElementById("toast-container");
  if (!existing) {
    const el = document.createElement("div");
    el.id = "toast-container";
    el.className = "toast-container";
    document.body.appendChild(el);
  }

  // --- Authentication & Role Handling ---
  try {
    const userData = get_parsed_jwt_identity();

    if (userData) {
      const navLogin = document.getElementById("nav-login");
      const navSignup = document.getElementById("nav-signup");
      const navLogout = document.getElementById("nav-logout");
      const navDashboard = document.getElementById("nav-dashboard");

      const userRole = userData.role;

      const token = localStorage.getItem("access_token");
      if (token && (userRole === "admin" || userRole === "chairman")) {
        // Background update of plan info
        fetch("/api/user/plan", {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Background-Request": "true",
          },
        })
          .then((res) => res.json())
          .then((planData) => {
            if (planData && planData.name) {
              const newTier = planData.name.toLowerCase();
              if (localStorage.getItem("user_subscription_tier") !== newTier) {
                localStorage.setItem("user_subscription_tier", newTier);
                initializeSidebar(userRole);
              }
            }
          })
          .catch(() => {});
      }

      localStorage.setItem("user_role", userRole);

      // Initialize Sidebar
      initializeSidebar(userRole);

      if (navLogin) navLogin.style.display = "none";
      if (navSignup) navSignup.style.display = "none";
      if (navLogout) navLogout.style.display = "list-item";
      if (navDashboard) {
        navDashboard.style.display = "list-item";
        const dashboardLink = navDashboard.querySelector("a");
        if (dashboardLink) {
          dashboardLink.href =
            userRole === "chairman"
              ? "superadmindashboard.html"
              : "admindashboard.html";
        }
      }

      const logoutButton = document.getElementById("logout-button");
      if (logoutButton) {
        logoutButton.addEventListener("click", () => {
          localStorage.clear();
          window.showToast("You have been logged out.", "info");
          setTimeout(() => {
            window.location.href = "/landing";
          }, 1500);
        });
      }

      // Workplace-specific terminology
      const workplaceType = userData.workplace_type || "School";
      const terminology = {
        School: {
          staff: "Teachers",
          member: "Students",
          members: "Students",
          parent: "Parents",
          parents: "Parents",
          child: "Student",
          children: "Students",
          class: "Class",
          classes: "Classes",
          section: "Section",
          attendance: "Attendance",
          management: "School Management",
          tools: "School Tools",
          add_member: "Add New Student/Teacher",
          staff_member: "Teacher/Staff Member",
          subject_spec: "Subject / Specialization",
          academic_records: "Student Profiles",
          performance_hub: "Academic Performance",
          library: "Library System",
          substitution: "Substitution Hub",
          hr_leaves: "Staff HR & Leaves",
        },
        Coaching: {
          staff: "Instructors",
          member: "Aspirants",
          members: "Aspirants",
          parent: "Guardians",
          parents: "Guardians",
          child: "Aspirant",
          children: "Aspirants",
          class: "Batch",
          classes: "Batches",
          section: "Group",
          attendance: "Attendance",
          management: "Coaching Management",
          tools: "Coaching Tools",
          add_member: "Add New Aspirant/Instructor",
          staff_member: "Instructor Member",
          subject_spec: "Subject / Expertise",
          academic_records: "Aspirant Profiles",
          performance_hub: "Test Results",
          library: "Resource Center",
          substitution: "Instructor Replacement",
          hr_leaves: "Instructor HR & Leaves",
        },
        Office: {
          staff: "Employees",
          member: "Team Members",
          members: "Team Members",
          parent: "Contact Person",
          parents: "Contact Persons",
          child: "Employee",
          children: "Employees",
          class: "Department",
          classes: "Departments",
          section: "Team",
          attendance: "Presence",
          management: "Office Management",
          tools: "Office Tools",
          add_member: "Add New Employee",
          staff_member: "Employee Member",
          subject_spec: "Role / Department",
          academic_records: "Employee Files",
          performance_hub: "KPIs & Appraisals",
          library: "Digital Assets",
          substitution: "Task Delegation",
          hr_leaves: "Employee HR & Leaves",
        },
        Hospital: {
          staff: "Medical Staff",
          member: "Patients",
          members: "Patients",
          parent: "Relatives",
          parents: "Relatives",
          child: "Patient",
          children: "Patients",
          class: "Ward",
          classes: "Wards",
          section: "Unit",
          attendance: "Rounds",
          management: "Hospital Management",
          tools: "Medical Tools",
          add_member: "Add New Patient/Staff",
          staff_member: "Medical Staff Member",
          subject_spec: "Specialization / Department",
          academic_records: "Patient Records",
          performance_hub: "Health Analytics",
          library: "Medical Library",
          substitution: "On-call Roster",
          hr_leaves: "Staff HR & Leaves",
        },
        Factory: {
          staff: "Supervisors",
          member: "Workers",
          members: "Workers",
          parent: "Emergency Contact",
          parents: "Emergency Contacts",
          child: "Worker",
          children: "Workers",
          class: "Line",
          classes: "Lines",
          section: "Shift",
          attendance: "Shift Record",
          management: "Factory Management",
          tools: "Production Tools",
          add_member: "Add New Worker/Supervisor",
          staff_member: "Supervisor Member",
          subject_spec: "Skill / Line",
          academic_records: "Worker Profiles",
          performance_hub: "Output Analytics",
          library: "Inventory Log",
          substitution: "Shift Reassignment",
          hr_leaves: "Worker HR & Leaves",
        },
        Gym: {
          staff: "Trainers",
          member: "Clients",
          members: "Clients",
          parent: "Spouse/Relative",
          parents: "Relatives",
          child: "Client",
          children: "Clients",
          class: "Program",
          classes: "Programs",
          section: "Batch",
          attendance: "Check-in",
          management: "Gym Management",
          tools: "Fitness Tools",
          add_member: "Add New Client/Trainer",
          staff_member: "Trainer Member",
          subject_spec: "Expertise / Program",
          academic_records: "Client Profiles",
          performance_hub: "Fitness Progress",
          library: "Equipment Log",
          substitution: "Trainer Replacement",
          hr_leaves: "Trainer HR & Leaves",
        },
        Hotel: {
          staff: "Staff",
          member: "Guest",
          members: "Guests",
          parent: "Sponsor",
          parents: "Sponsors",
          child: "Guest",
          children: "Guests",
          class: "Room Type",
          classes: "Room Types",
          section: "Floor",
          attendance: "Check-in Status",
          management: "Hotel Management",
          tools: "Service Tools",
          add_member: "Add New Guest/Staff",
          staff_member: "Staff Member",
          subject_spec: "Department / Role",
          academic_records: "Guest History",
          performance_hub: "Service Quality",
          library: "Facility Assets",
          substitution: "Duty Rotation",
          hr_leaves: "Staff HR & Leaves",
        },
      };

      const terms = terminology[workplaceType] || terminology.School;

      document.querySelectorAll("[data-i18n]").forEach((el) => {
        const key = el.getAttribute("data-i18n");
        if (terms[key]) {
          if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
            if (el.placeholder) {
              el.placeholder = el.placeholder.replace(/Student|Teacher|Staff|Parent|Member|Class|Timetable/g, terms[key]);
            }
          } else {
            // Preserve icons if present
            const icon = el.querySelector('i.fas, i.far, i.fab, i.fa-solid');
            if (icon) {
              const iconHtml = icon.outerHTML;
              el.innerHTML = `${iconHtml} ${terms[key]}`;
            } else {
              el.textContent = terms[key];
            }
          }
        }
      });
    }
  } catch {
    // Fallback for public state
    const navLogin = document.getElementById("nav-login");
    if (navLogin) navLogin.style.display = "list-item";
  }
});

/**
 * Global Fetch Wrapper with Integrated Preloader
 */
window.fetch = async function (input, init = {}) {
  let isBackground = false;
  let hadAuthHeader = false;
  if (init.headers) {
    const headers = init.headers;
    isBackground =
      headers["X-Background-Request"] === "true" ||
      headers["x-background-request"] === "true";
    const auth =
      headers["Authorization"] ||
      headers["authorization"];
    hadAuthHeader = !!(auth && String(auth).startsWith("Bearer "));
  }

  if (!isBackground) showGlobalPreloader();

  try {
    const response = await originalFetch(input, init);

    if (response.status === 401) {
      const hasStoredToken = !!localStorage.getItem("access_token");
      if (hadAuthHeader && hasStoredToken) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user_role");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("username");
        const path = window.location.pathname;
        if (path !== "/login" && path !== "/landing" && path !== "/") {
          window.location.href = "/login";
        }
      }
    }

    return response;
  } finally {
    if (!isBackground) hideGlobalPreloader();
  }
};

// ============================================================================
// LukaDataCache — client-side TTL cache with stale-while-revalidate (Phase 2.2)
// ============================================================================
(function (global) {
  'use strict';

  const DEFAULT_TTL_MS = 5 * 1000;
  const STATIC_TTL_MS = 30 * 1000;
  const LONG_TTL_MS = 2 * 60 * 1000;
  const MAX_ENTRIES = 512;

  const store = new Map();

  function now() {
    return Date.now();
  }

  function evictIfNeeded() {
    if (store.size <= MAX_ENTRIES) return;
    const toRemove = store.size - MAX_ENTRIES;
    let removed = 0;
    for (const key of store.keys()) {
      if (removed >= toRemove) break;
      store.delete(key);
      removed++;
    }
  }

  function standardKey(endpoint, params) {
    let base = endpoint;
    if (params) {
      const str = typeof params === 'string'
        ? params
        : JSON.stringify(params, Object.keys(params || {}).sort());
      base = base + '::' + str;
    }
    // Prefix with institution if available for tenant isolation
    try {
      const raw = localStorage.getItem('user_identity');
      if (raw) {
        const ident = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (ident && ident.institution_id) {
          base = `inst:${ident.institution_id}::${base}`;
        }
      }
    } catch (_id) { void _id;
      /* ignore */
    }
    return base;
  }

  function get(key) {
    const entry = store.get(key);
    if (!entry) return undefined;
    if (now() > entry.expiresAt) {
      // Entry exists but is stale. Caller may still want it for SWR.
      return { stale: true, value: entry.value, expiresAt: entry.expiresAt };
    }
    return { stale: false, value: entry.value, expiresAt: entry.expiresAt };
  }

  function set(key, value, ttlMs) {
    const ttl = typeof ttlMs === 'number' ? ttlMs : DEFAULT_TTL_MS;
    store.set(key, { value, expiresAt: now() + ttl });
    evictIfNeeded();
  }

  /**
   * Primary API: fetch via global.fetch, using SWR cache pattern.
   * - Cache HIT (fresh): return cached value immediately, no revalidation.
   * - Cache HIT (stale, within extra window): return cached value IMMEDIATELY,
   *   then fire background revalidation fetch (stored into cache).
   * - Cache MISS (or stale too long): fetch, cache, return value.
   */
  async function fetchCached(endpoint, fetchOptions, opts) {
    opts = opts || {};
    const key = standardKey(endpoint, opts.params);
    const ttl = opts.ttlMs != null ? opts.ttlMs : DEFAULT_TTL_MS;
    const staleExtra = opts.staleWhileRevalidateMs != null
      ? opts.staleWhileRevalidateMs
      : 25 * 1000;

    const hit = get(key);

    // Case 1: fresh hit within TTL
    if (hit && !hit.stale) {
      try {
        const cloned = JSON.parse(JSON.stringify(hit.value));
        return cloned;
      } catch (_c1) { void _c1;
        return hit.value;
      }
    }

    // Case 2: stale hit within revalidation window — return stale, kick off bg fetch
    if (hit && hit.stale && now() <= hit.expiresAt + staleExtra) {
      const staleValue = hit.value;
      // Fire and forget the background revalidation
      Promise.resolve()
        .then(() => originalFetch(endpoint, fetchOptions || {}))
        .then((r) => {
          if (!r.ok) throw new Error('bg fetch not ok');
          return r.json();
        })
        .then((fresh) => set(key, fresh, ttl))
        .catch(() => {
          /* bg revalidation failures are swallowed */
        });
      try {
        return JSON.parse(JSON.stringify(staleValue));
      } catch (_c2) { void _c2;
        return staleValue;
      }
    }

    // Case 3: miss or stale expired — fetch fresh now
    const resp = await global.fetch(endpoint, fetchOptions || {});
    let body;
    try {
      body = await resp.json();
    } catch (_c3) { void _c3;
      body = null;
    }
    if (resp.ok && body) {
      set(key, body, ttl);
    }
    return body;
  }

  function invalidatePattern(prefix) {
    if (!prefix) return clear();
    let count = 0;
    for (const key of Array.from(store.keys())) {
      if (key.indexOf(prefix) === 0) {
        store.delete(key);
        count++;
      }
    }
    return count;
  }

  function clear() {
    const count = store.size;
    store.clear();
    return count;
  }

  global.LukaDataCache = {
    TTL_DEFAULT: DEFAULT_TTL_MS,
    TTL_STATIC: STATIC_TTL_MS,
    TTL_LONG: LONG_TTL_MS,
    get,
    set,
    fetchCached,
    key: standardKey,
    invalidatePattern,
    clear,
    _size: () => store.size,
  };
})(window);

// ============================================================================
// Sidebar Hover Prefetch (Phase 2.7) — 150ms hover → fire → LukaDataCache
// ============================================================================
(function (global) {
  'use strict';

  const HOVER_DELAY_MS = 150;
  const timers = new WeakMap();
  const fired = new Set();

  // Map of URL path → API endpoint(s) to prefetch when user hovers sidebar link
  const PAGE_TO_API = {
    '/member_management': [
      { endpoint: '/api/members/members/summary', ttlMs: 60 * 1000 },
      { endpoint: '/api/members/members?page_size=50', ttlMs: 5 * 1000 },
    ],
    '/audit-logs': [
      { endpoint: '/api/audit/logs/summary', ttlMs: 60 * 1000 },
      { endpoint: '/api/audit/logs?page_size=50', ttlMs: 5 * 1000 },
    ],
    '/manage_tickets': [
      { endpoint: '/api/support/admin/tickets/summary', ttlMs: 60 * 1000 },
      { endpoint: '/api/support/admin/tickets?page_size=50', ttlMs: 5 * 1000 },
    ],
    '/attendance_reports': [
      { endpoint: '/api/admin/dashboard-data', ttlMs: 30 * 1000 },
    ],
    '/timetable_management': [
      { endpoint: '/api/timetable', ttlMs: 30 * 1000 },
    ],
    '/admindashboard': [
      { endpoint: '/api/admin/dashboard-data', ttlMs: 30 * 1000 },
    ],
    '/erp_management': [
      { endpoint: '/api/erp/classrooms', ttlMs: 30 * 1000 },
      { endpoint: '/api/erp/subjects', ttlMs: 30 * 1000 },
    ],
  };

  function authHeaders() {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    return {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
      'X-Background-Request': 'true',
    };
  }

  function firePrefetch(pathname) {
    const rules = PAGE_TO_API[pathname];
    if (!rules || !rules.length) return;
    const hdrs = authHeaders();
    if (!hdrs) return;
    for (const rule of rules) {
      const key = window.LukaDataCache.key(rule.endpoint);
      if (fired.has(key)) continue;
      fired.add(key);
      // 5 min cool-down before re-prefetching identical endpoint
      setTimeout(() => fired.delete(key), 5 * 60 * 1000);
      window.LukaDataCache
        .fetchCached(rule.endpoint, { headers: hdrs }, { ttlMs: rule.ttlMs })
        .catch(() => {
          /* prefetch fail is silent */
        });
    }
  }

  function onEnter(el, pathname) {
    if (timers.has(el)) return;
    const t = setTimeout(() => firePrefetch(pathname), HOVER_DELAY_MS);
    timers.set(el, t);
  }

  function onLeave(el) {
    const t = timers.get(el);
    if (t) {
      clearTimeout(t);
      timers.delete(el);
    }
  }

  function attach() {
    try {
      const sidebarLinks = document.querySelectorAll(
        'nav.sidebar a, .sidebar a, #sidebar a, aside a, [data-sidebar-link]'
      );
      sidebarLinks.forEach((a) => {
        if (!a || a.dataset.prefetchBound === '1') return;
        let target = a.getAttribute('href') || '';
        if (!target || target.startsWith('#') || target.startsWith('http')) return;
        if (target.indexOf('?') >= 0) target = target.slice(0, target.indexOf('?'));
        a.addEventListener('mouseenter', () => onEnter(a, target));
        a.addEventListener('mouseleave', () => onLeave(a));
        a.addEventListener('focus', () => onEnter(a, target));
        a.addEventListener('blur', () => onLeave(a));
        a.dataset.prefetchBound = '1';
      });
    } catch (_sp) { void _sp;
      /* no sidebar on this page */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
    // Re-attach after a short delay in case sidebar renders later (SPA-like)
    setTimeout(attach, 1500);
    setTimeout(attach, 5000);
  }

  global.LukaSidebarPrefetch = { attach, firePrefetch, PAGE_TO_API };
})(window);

// ============================================================================
// Frontend Perf Beacon (Phase 3.1) — best-effort; POST every 100th sample
// ============================================================================
(function (global) {
  'use strict';

  let sampleCounter = 0;
  const DEV_MODE =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const SAMPLE_EVERY_N = DEV_MODE ? 1 : 100;
  const ENDPOINT = '/api/metrics/frontend-perf';

  function detectRoute() {
    const path = window.location.pathname || '';
    const last = path.split('/').filter(Boolean).pop();
    return last || 'landing';
  }

  function connInfo() {
    const c = global.navigator && global.navigator.connection;
    return c ? c.effectiveType : 'n/a';
  }

  function deviceMem() {
    return global.navigator && typeof global.navigator.deviceMemory === 'number'
      ? global.navigator.deviceMemory
      : 'n/a';
  }

  function getInstitutionId() {
    try {
      const raw = localStorage.getItem('user_identity');
      if (raw) {
        const ident = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (ident && ident.institution_id) return ident.institution_id;
      }
    } catch (_gi) { void _gi;
      /* ignore */
    }
    return undefined;
  }

  function record(sample) {
    try {
      sampleCounter++;
      if (!DEV_MODE && sampleCounter % SAMPLE_EVERY_N !== 0) return;
      if (!sample || typeof sample !== 'object') return;

      const payload = Object.assign(
        {
          route: detectRoute(),
          connectionEffectiveType: connInfo(),
          deviceMemory_gb: deviceMem(),
          userAgent: navigator.userAgent,
          institution_id: getInstitutionId(),
        },
        sample
      );

      const hdrs = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('access_token');
      if (token) hdrs.Authorization = 'Bearer ' + token;

      originalFetch(ENDPOINT, {
        method: 'POST',
        headers: hdrs,
        body: JSON.stringify(payload),
        keepalive: typeof navigator.sendBeacon === 'undefined',
      }).catch(() => {
        /* fire-and-forget: never surface to user */
      });
    } catch (_rec) { void _rec;
      /* absolutely never surface to user */
    }
  }

  function measureTTI(markerName) {
    let tti = undefined;
    try {
      const nowTs = performance.now();
      tti = Math.round(nowTs);
      if (typeof performance.measure === 'function' && markerName) {
        try {
          performance.measure(markerName, {
            start: 0,
            end: nowTs,
            detail: { metric: 'timeFromNavigationStartToFirstInteractiveRow_ms' },
          });
        } catch (_measureModern) { void _measureModern;
          try {
            if (typeof performance.mark === 'function') {
              performance.mark('__lukaperf_end_' + markerName);
              try {
                performance.measure(markerName, 'navigationStart', '__lukaperf_end_' + markerName);
              } catch (_eNS) { void _eNS;
                try {
                  performance.measure(markerName, '__lukaperf_end_' + markerName);
                } catch (_e2) { void _e2; }
              }
              try { performance.clearMarks('__lukaperf_end_' + markerName); } catch (_c) { void _c; }
            }
          } catch (_measureFallback) { void _measureFallback;
            /* ignore measure errors */
          }
        }
      }
    } catch (_tti) { void _tti;
      /* ignore */
    }
    return tti;
  }

  function recordFirstInteractiveRow(extra) {
    try {
      const MARKER = 'timeFromNavigationStartToFirstInteractiveRow_ms';
      const tti = measureTTI(MARKER);
      const sample = Object.assign(
        { [MARKER]: tti },
        extra || {}
      );
      record(sample);
      return tti;
    } catch (_rfir) { void _rfir;
      return undefined;
    }
  }

  global.LukaPerfBeacon = {
    record,
    measureTTI,
    recordFirstInteractiveRow,
    DEV_MODE,
    SAMPLE_EVERY_N,
  };
})(window);
